import { Link, useParams, Navigate } from "react-router-dom";
import Seo from "../components/Seo";
import { MarketingHeader, CTASection } from "../components/MarketingShell";
import LegalFooter from "../components/LegalFooter";
import { POSTS } from "../lib/blogPosts";
import { ArrowLeft, Clock, ArrowRight } from "lucide-react";

export function BlogIndex() {
  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path="/blog" title="Blog" description="LinguaSub blog — deep dives on subtitle translation, Hindi/Indian language localisation, YouTube growth for creators, and the AI models behind personality-preserving translation." />
      <MarketingHeader />

      <section className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-16 md:py-24">
          <div className="text-[10px] font-mono uppercase tracking-widest text-blue-400 mb-3">Writing</div>
          <h1 className="font-heading text-4xl md:text-6xl font-black tracking-tighter leading-tight mb-4 text-white">
            LinguaSub Blog.
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed max-w-2xl">
            Notes on subtitle translation, Indian-language localisation, and how creators use LinguaSub to grow.
          </p>
        </div>
      </section>

      <section className="border-b border-zinc-800">
        <div className="max-w-4xl mx-auto px-4 md:px-8 py-12 md:py-16">
          <div className="space-y-8">
            {POSTS.map((post) => (
              <Link key={post.slug} to={`/blog/${post.slug}`} className="block border border-zinc-800 bg-zinc-900/40 p-6 md:p-8 hover:border-blue-500/40 transition-colors group">
                <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-3">
                  <span>{post.date}</span>
                  <span>·</span>
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
                </div>
                <h2 className="font-heading text-2xl md:text-3xl font-black tracking-tight text-white mb-3 group-hover:text-blue-400 transition-colors">{post.title}</h2>
                <p className="text-sm text-zinc-400 leading-relaxed mb-4">{post.excerpt}</p>
                <div className="text-xs font-mono uppercase tracking-widest text-blue-400 inline-flex items-center gap-1">
                  Read <ArrowRight className="w-3 h-3 group-hover:translate-x-0.5 transition-transform" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><LegalFooter /></div>
    </div>
  );
}

export function BlogPost() {
  const { slug } = useParams();
  const post = POSTS.find((p) => p.slug === slug);
  if (!post) return <Navigate to="/blog" replace />;

  return (
    <div className="grain-bg min-h-screen bg-zinc-950 text-white">
      <Seo path={`/blog/${post.slug}`} title={post.title} description={post.excerpt} />
      <MarketingHeader />

      <article className="border-b border-zinc-800">
        <div className="max-w-3xl mx-auto px-4 md:px-8 py-12 md:py-20">
          <Link to="/blog" className="inline-flex items-center gap-2 text-xs font-mono uppercase tracking-widest text-zinc-500 hover:text-white mb-8">
            <ArrowLeft className="w-3 h-3" /> Back to blog
          </Link>
          <div className="flex items-center gap-3 text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-4">
            <span>{post.date}</span>
            <span>·</span>
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.readTime}</span>
          </div>
          <h1 className="font-heading text-3xl md:text-5xl font-black tracking-tighter leading-tight mb-6 text-white">
            {post.title}
          </h1>
          <p className="text-zinc-400 text-lg leading-relaxed mb-10">{post.excerpt}</p>
          <div className="prose-lingua space-y-5">
            {post.body.map((block, idx) => (
              block.h
                ? <h2 key={`h-${idx}-${block.h.slice(0, 20)}`} className="font-heading text-xl md:text-2xl font-bold text-white pt-4 mb-2">{block.h}</h2>
                : <p key={`p-${idx}-${(block.p || "").slice(0, 20)}`} className="text-base text-zinc-300 leading-relaxed">{block.p}</p>
            ))}
          </div>
        </div>
      </article>

      <CTASection title="Try LinguaSub on your own video." subtitle="One-time free trial for new users. Sign in with Google or email." />
      <div className="max-w-6xl mx-auto px-4 md:px-8"><LegalFooter /></div>
    </div>
  );
}
